import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Plus, Trash2, Play, Calendar, CheckCircle2, XCircle, Clock, ArrowRight, Power, AlertCircle } from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { useProjects } from "@/context/ProjectContext";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function SchedulerPage() {
  const { active } = useProjects();
  const [schedules, setSchedules] = useState([]);
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [runs, setRuns] = useState({});
  const [running, setRunning] = useState(null);

  // form state
  const [name, setName] = useState("");
  const [cadence, setCadence] = useState("daily");
  const [hour, setHour] = useState(9);
  const [dayOfWeek, setDayOfWeek] = useState(1);
  const [autoBroadcast, setAutoBroadcast] = useState(false);

  const refresh = async () => {
    if (!active) return;
    const { data } = await api.get(`/schedules?project_id=${active.id}`);
    setSchedules(data);
  };

  useEffect(() => { refresh(); }, [active?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async (e) => {
    e?.preventDefault();
    setCreating(true); setError("");
    try {
      const body = {
        project_id: active.id,
        kind: "blog_drafter",
        cadence, hour_utc: parseInt(hour),
        day_of_week: cadence === "weekly" ? parseInt(dayOfWeek) : null,
        auto_broadcast: autoBroadcast,
        name: name || null,
      };
      await api.post("/schedules", body);
      setOpen(false);
      setName(""); setCadence("daily"); setHour(9); setDayOfWeek(1); setAutoBroadcast(false);
      refresh();
    } catch (e2) { setError(formatApiError(e2)); }
    finally { setCreating(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this schedule?")) return;
    await api.delete(`/schedules/${id}`).catch(() => {});
    refresh();
  };

  const toggle = async (s) => {
    await api.patch(`/schedules/${s.id}`, { enabled: !s.enabled }).catch(() => {});
    refresh();
  };

  const runNow = async (id) => {
    setRunning(id);
    try {
      await api.post(`/schedules/${id}/run-now`);
      // poll runs for ~30s
      let tries = 0;
      const interval = setInterval(async () => {
        await loadRuns(id);
        if (++tries > 20) clearInterval(interval);
      }, 3000);
    } catch {}
    finally { setTimeout(() => { setRunning(null); refresh(); }, 1500); }
  };

  const loadRuns = async (id) => {
    try {
      const { data } = await api.get(`/schedules/${id}/runs`);
      setRuns((r) => ({ ...r, [id]: data }));
    } catch {}
  };

  const toggleExpand = (id) => {
    if (expanded === id) {
      setExpanded(null);
    } else {
      setExpanded(id);
      if (!runs[id]) loadRuns(id);
    }
  };

  if (!active) {
    return (
      <DashboardLayout eyebrow="Scheduler" title="Set it and forget it." subtitle="Auto-draft + auto-broadcast new blog posts on a schedule.">
        <div className="bg-zinc-950 border border-dashed border-zinc-800 rounded-sm p-16 text-center" data-testid="scheduler-no-project">
          <Calendar className="w-10 h-10 mx-auto mb-4 text-zinc-700" />
          <h3 className="font-display text-xl mb-2">No project selected</h3>
          <p className="text-zinc-400 mb-6">Add a project to schedule auto-drafting.</p>
          <Button asChild className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm">
            <Link to="/onboarding">Add project <ArrowRight className="w-4 h-4 ml-1.5" /></Link>
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      eyebrow="Scheduler"
      title="Set it and forget it."
      subtitle="Auto-draft a new post from your content plan on a recurring schedule. Optionally broadcast each draft to all linked social channels."
      action={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm h-10" data-testid="scheduler-new-btn">
              <Plus className="w-4 h-4 mr-1.5" /> New schedule
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0B0B0E] border-zinc-800 rounded-sm max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-display">Create a schedule</DialogTitle>
            </DialogHeader>
            <form onSubmit={create} className="space-y-4">
              <div>
                <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Name (optional)</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Daily morning drafter"
                       className="h-11 bg-zinc-950 border-zinc-800 rounded-sm focus-visible:ring-[#0055FF]" data-testid="sched-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Cadence</Label>
                  <Select value={cadence} onValueChange={setCadence}>
                    <SelectTrigger className="h-11 bg-zinc-950 border-zinc-800 rounded-sm" data-testid="sched-cadence-trigger"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0B0B0E] border-zinc-800 rounded-sm">
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Hour (UTC)</Label>
                  <Select value={String(hour)} onValueChange={(v) => setHour(parseInt(v))}>
                    <SelectTrigger className="h-11 bg-zinc-950 border-zinc-800 rounded-sm" data-testid="sched-hour-trigger"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0B0B0E] border-zinc-800 rounded-sm max-h-60">
                      {Array.from({ length: 24 }, (_, i) => (
                        <SelectItem key={i} value={String(i)}>{String(i).padStart(2, "0")}:00</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {cadence === "weekly" && (
                <div>
                  <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Day of week</Label>
                  <Select value={String(dayOfWeek)} onValueChange={(v) => setDayOfWeek(parseInt(v))}>
                    <SelectTrigger className="h-11 bg-zinc-950 border-zinc-800 rounded-sm" data-testid="sched-dow-trigger"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0B0B0E] border-zinc-800 rounded-sm">
                      {DAYS.map((d, i) => <SelectItem key={d} value={String(i)}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="flex items-center justify-between bg-zinc-900 border border-zinc-800 rounded-sm px-3 py-2.5">
                <div>
                  <span className="text-sm text-zinc-200">Auto-broadcast after drafting</span>
                  <p className="text-xs text-zinc-500 mt-0.5">Fan out to all enabled social channels</p>
                </div>
                <Switch checked={autoBroadcast} onCheckedChange={setAutoBroadcast} data-testid="sched-broadcast-toggle" />
              </div>
              {error && <div className="text-sm text-red-400" data-testid="sched-error">{error}</div>}
              <DialogFooter>
                <Button type="submit" disabled={creating} className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm" data-testid="sched-submit">
                  {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create schedule"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      }
    >
      <div className="bg-amber-950/10 border border-amber-900/30 rounded-sm p-3 mb-6 flex items-start gap-2.5" data-testid="sched-prereq">
        <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
        <p className="text-xs text-amber-200/80 leading-relaxed">
          Prereqs: this project needs an active 30-day Content Plan. Schedules pull the next "planned" post from the latest plan, so generate one first via <Link to="/content-plan" className="text-[#3377FF] hover:underline">Content Calendar</Link> or <Link to="/autopilot" className="text-[#3377FF] hover:underline">Autopilot</Link>.
        </p>
      </div>

      {schedules.length === 0 ? (
        <div className="bg-zinc-950 border border-dashed border-zinc-800 rounded-sm p-16 text-center" data-testid="sched-empty">
          <Calendar className="w-10 h-10 mx-auto mb-4 text-zinc-700" />
          <h3 className="font-display text-xl mb-2">No schedules yet</h3>
          <p className="text-zinc-400">Create your first to put content drafting on autopilot.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="sched-list">
          {schedules.map((s) => (
            <div key={s.id} className="bg-zinc-950 border border-zinc-800 rounded-sm" data-testid={`sched-${s.id}`}>
              <div className="px-6 py-5 flex items-start gap-4">
                <div className={`w-10 h-10 rounded-sm grid place-items-center flex-shrink-0 ${s.enabled ? "bg-[#0055FF]/10 border border-[#0055FF]/30" : "bg-zinc-900 border border-zinc-800"}`}>
                  <Calendar className={`w-5 h-5 ${s.enabled ? "text-[#0055FF]" : "text-zinc-600"}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-medium text-zinc-100">{s.name}</span>
                    <Badge variant="outline" className="rounded-sm border-zinc-700 text-zinc-400 text-[10px] uppercase">
                      {s.cadence}{s.cadence === "weekly" ? ` · ${DAYS[s.day_of_week ?? 0]}` : ""} · {String(s.hour_utc).padStart(2, "0")}:00 UTC
                    </Badge>
                    {s.auto_broadcast && <Badge className="rounded-sm bg-[#10B981]/15 text-[#10B981] border-[#10B981]/30 text-[10px] uppercase">+ Broadcast</Badge>}
                    {!s.enabled && <Badge variant="outline" className="rounded-sm border-zinc-700 text-zinc-500 text-[10px] uppercase">Paused</Badge>}
                  </div>
                  <div className="text-xs text-zinc-500 flex items-center gap-3 flex-wrap">
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> Next run {s.next_run_at ? new Date(s.next_run_at).toLocaleString() : "—"}</span>
                    <span>· Runs: {s.run_count ?? 0}</span>
                    {s.last_run && (
                      <span className="flex items-center gap-1">·
                        {s.last_run.status === "done" ? <CheckCircle2 className="w-3 h-3 text-[#10B981]" /> : <XCircle className="w-3 h-3 text-[#EF4444]" />}
                        Last: {s.last_run.status}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button onClick={() => toggle(s)} size="sm" variant="ghost"
                          className={`rounded-sm ${s.enabled ? "text-[#10B981] hover:bg-[#10B981]/10" : "text-zinc-500 hover:bg-zinc-900"}`}
                          data-testid={`sched-toggle-${s.id}`}>
                    <Power className="w-3.5 h-3.5" />
                  </Button>
                  <Button onClick={() => runNow(s.id)} disabled={running === s.id} size="sm" variant="outline"
                          className="bg-zinc-950 hover:bg-zinc-900 border-zinc-800 rounded-sm text-xs"
                          data-testid={`sched-run-${s.id}`}>
                    {running === s.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Play className="w-3 h-3 mr-1" /> Run now</>}
                  </Button>
                  <Button onClick={() => toggleExpand(s.id)} size="sm" variant="outline"
                          className="bg-zinc-950 hover:bg-zinc-900 border-zinc-800 rounded-sm text-xs"
                          data-testid={`sched-history-${s.id}`}>
                    {expanded === s.id ? "Hide" : "History"}
                  </Button>
                  <Button onClick={() => remove(s.id)} size="sm" variant="ghost"
                          className="text-zinc-500 hover:text-[#EF4444] hover:bg-zinc-900 rounded-sm"
                          data-testid={`sched-delete-${s.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
              {expanded === s.id && (
                <div className="border-t border-zinc-800 bg-[#09090B]" data-testid={`sched-runs-${s.id}`}>
                  {(runs[s.id] || []).length === 0 ? (
                    <div className="px-6 py-6 text-sm text-zinc-500 text-center">No runs yet.</div>
                  ) : (
                    <ul className="divide-y divide-zinc-900">
                      {(runs[s.id] || []).map((r) => (
                        <li key={r.id} className="px-6 py-3 flex items-center gap-3 text-sm" data-testid={`run-${r.id}`}>
                          {r.status === "done" ? <CheckCircle2 className="w-4 h-4 text-[#10B981]" /> :
                           r.status === "running" ? <Loader2 className="w-4 h-4 animate-spin text-[#0055FF]" /> :
                           <XCircle className="w-4 h-4 text-[#EF4444]" />}
                          <span className="text-zinc-300 truncate flex-1">
                            {r.result?.title || r.result?.error || (r.status === "running" ? "Running…" : r.status)}
                          </span>
                          {r.result?.broadcast && (
                            <Badge variant="outline" className="rounded-sm border-zinc-700 text-zinc-400 text-[10px]">
                              {r.result.broadcast.ok_count}/{r.result.broadcast.total} sent
                            </Badge>
                          )}
                          <span className="text-xs text-zinc-600 font-mono">{new Date(r.started_at).toLocaleString()}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </DashboardLayout>
  );
}
